import { useEffect, useRef, type RefObject } from 'react';

/**
 * Хук для обработки кликов вне элемента.
 * Слушатель добавляется сразу — клик по anchor исключается через excludeRefs.
 */
export function useClickOutside(
  ref: RefObject<HTMLElement | null>,
  onOutside: (e: MouseEvent) => void,
  options: {
    /** Элементы, клики по которым не считаются "вне" */
    excludeRefs?: RefObject<HTMLElement | null>[];
    /** Селектор: клики по элементу или его предкам не считаются "вне" */
    excludeSelector?: string;
    /** Не закрывать (например, пока идёт DnD файлов с рабочего стола) */
    shouldIgnoreOutside?: () => boolean;
    /** Активен ли хук */
    enabled?: boolean;
    /** Использовать capture-фазу (по умолчанию true — обрабатываем до целевого элемента) */
    capture?: boolean;
  } = {}
) {
  const { excludeRefs = [], excludeSelector, shouldIgnoreOutside, enabled = true, capture = true } = options;
  const onOutsideRef = useRef(onOutside);
  const excludeRefsRef = useRef(excludeRefs);
  const excludeSelectorRef = useRef(excludeSelector);
  const shouldIgnoreOutsideRef = useRef(shouldIgnoreOutside);
  onOutsideRef.current = onOutside;
  excludeRefsRef.current = excludeRefs;
  excludeSelectorRef.current = excludeSelector;
  shouldIgnoreOutsideRef.current = shouldIgnoreOutside;

  useEffect(() => {
    if (!enabled || !ref) return;

    const handleOutside = (e: MouseEvent) => {
      if (shouldIgnoreOutsideRef.current?.()) return;
      const el = ref.current;
      const target = e.target as Node;
      const excludedByRef = excludeRefsRef.current.some(
        (r) => r?.current && r.current.contains(target)
      );
      const excludedBySelector =
        excludeSelectorRef.current &&
        (target as Element).closest?.(excludeSelectorRef.current);
      if (!el?.contains(target) && !excludedByRef && !excludedBySelector) {
        onOutsideRef.current(e);
      }
    };

    const addListeners = () => {
      document.addEventListener('mousedown', handleOutside, capture);
      document.addEventListener('keydown', handleEscape);
    };

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onOutsideRef.current(e as unknown as MouseEvent);
    };

    addListeners();
    return () => {
      document.removeEventListener('mousedown', handleOutside, capture);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [enabled, ref, capture]);
}

